import { useEffect, useState } from 'react';
import { useBranchStore } from '@/stores';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, Users, Store, CreditCard, Wallet } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import { ExportButtons } from '@/components/ExportButtons';
import BranchLink from '@/components/BranchLink';
import api from '@/lib/api';
import { toast } from 'sonner';
import { localToday, toLocalYmd } from '@/lib/dateRange';

const SR = (n) => (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function SalesSummaryPage() {
  const [data, setData] = useState(null);
  const [startDate, setStartDate] = useState(toLocalYmd(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [endDate, setEndDate] = useState(localToday());
  const posBranch = useBranchStore((s) => s.activeBranchId);
  const setPosBranch = useBranchStore((s) => s.setActiveBranchId);
  const [branchId, setBranchId] = useState(posBranch || 'all');
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  // iter962 — Follow the shared POS/Stock active branch (branchStore).
  useEffect(() => { setBranchId(posBranch || 'all'); }, [posBranch]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      if (branchId && branchId !== 'all') params.set('branch_id', branchId);
      const res = await api.get(`/accounting/sales-summary?${params.toString()}`);
      setData(res.data);
    } catch { toast.error('Failed to load sales summary'); }
    finally { setLoading(false); }
  };

  useEffect(() => { api.get('/branches').then(r => setBranches(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [branchId]);

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="sales-summary-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <TrendingUp size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Sales Summary</h1>
              <p className="text-xs text-muted-foreground">Roll-up by day, branch, customer, and payment mode</p>
            </div>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Select value={branchId} onValueChange={(v) => { setBranchId(v); setPosBranch(v === 'all' ? null : v); }}>
              <SelectTrigger className="w-44 h-9 text-xs" data-testid="ss-branch-filter"><SelectValue placeholder="All Branches" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-36" />
            <span className="text-muted-foreground">to</span>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-36" />
            <Button size="sm" onClick={fetchData} data-testid="ss-generate-btn">Apply</Button>
            <ExportButtons branchIds={branchId && branchId !== 'all' ? [branchId] : []} dataType="sales_summary" filters={{ branch_id: branchId === 'all' ? null : branchId }} startDate={startDate} endDate={endDate} testid="ss-export" />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MiniKPI label="Net Sales" value={SR(data.totals.net)} icon={TrendingUp} color="emerald" testid="ss-kpi-net" />
              <MiniKPI label="Transactions" value={data.totals.count} icon={CreditCard} color="info" testid="ss-kpi-count" />
              <MiniKPI label="Avg. Sale" value={SR(data.totals.avg_sale)} icon={Wallet} color="purple" testid="ss-kpi-avg" />
              <MiniKPI label="Discounts" value={SR(data.totals.discounts)} icon={TrendingUp} color="warning" testid="ss-kpi-disc" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Store size={14} /> By Branch</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-muted/50"><th className="text-left p-2">Branch</th><th className="text-right p-2">Count</th><th className="text-right p-2">Total</th></tr></thead>
                    <tbody>
                      {data.by_branch.map(r => (
                        <tr key={r.branch_id} className="border-t"><td className="p-2"><BranchLink branchId={r.branch_id} name={r.branch_name} /></td><td className="p-2 text-right">{r.count}</td><td className="p-2 text-right font-mono font-semibold text-success">{SR(r.total)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><CreditCard size={14} /> By Payment Mode</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-muted/50"><th className="text-left p-2">Mode</th><th className="text-right p-2">Total</th><th className="text-right p-2">%</th></tr></thead>
                    <tbody>
                      {data.by_payment_mode.map(r => {
                        const pct = data.totals.net > 0 ? (r.total / data.totals.net * 100) : 0;
                        return <tr key={r.mode} className="border-t"><td className="p-2 capitalize">{r.mode.replace('_', ' ')}</td><td className="p-2 text-right font-mono">{SR(r.total)}</td><td className="p-2 text-right text-muted-foreground">{pct.toFixed(1)}%</td></tr>;
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users size={14} /> Top 10 Customers</CardTitle></CardHeader>
              <CardContent className="p-0">
                {data.by_customer_top10.length === 0 ? (
                  <p className="p-6 text-center text-xs text-muted-foreground">No customer-linked sales in this period (all were walk-ins).</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead><tr className="bg-muted/50"><th className="text-left p-2">#</th><th className="text-left p-2">Customer</th><th className="text-right p-2">Orders</th><th className="text-right p-2">Revenue</th></tr></thead>
                    <tbody>
                      {data.by_customer_top10.map((r, i) => (
                        <tr key={r.customer_id} className="border-t"><td className="p-2 text-muted-foreground">{i + 1}</td><td className="p-2 font-medium">{r.customer_name}</td><td className="p-2 text-right">{r.count}</td><td className="p-2 text-right font-mono font-semibold">{SR(r.total)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

const MiniKPI = ({ label, value, icon: Icon, color, testid }) => {
  const bg = { emerald: 'from-emerald-50 to-teal-50 border-emerald-200', info: 'from-blue-50 to-cyan-50 border-blue-200', purple: 'from-violet-50 to-purple-50 border-violet-200', warning: 'from-amber-50 to-yellow-50 border-amber-200' }[color] || '';
  return (
    <Card className={`border bg-gradient-to-br ${bg}`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2"><Icon size={16} className="text-muted-foreground" /></div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
        <p className="text-xl font-bold mt-1 inline-flex items-center gap-1">{typeof value === 'number' ? value : <><RiyalIcon size="0.7em" /> {value}</>}</p>
      </CardContent>
    </Card>
  );
};
